// SEQ-180 phase 2, task P17: everything the widget editor drawer decides,
// with no React in it, so the rules are provable under Deno and the drawer
// only renders them.
//
// THE DERIVED CONTROLS PRINCIPLE (plan section 7.2). The aggregation control
// offers only what the selected metric permits, so an invalid pair cannot be
// composed and then rejected. Same for the secondary metric field, which
// only exists on the chart types that can draw two series. Validation is
// still run on every draft, because a config can also arrive from a saved
// row written by an older or newer version.

import {
  monthOnMonthMetricKeys,
  PHASE1_WIDGETS,
  permittedAggregationsFor,
  validateWidgetConfig,
  validateWidgetMetricCompatibility,
  type PnlAggregation,
  type PnlChartType,
  type PnlComparisonKind,
  type PnlWidgetConfig,
  type PnlWidgetGroupBy,
  type PnlWidgetIssue,
  type PnlWidgetIssueField,
  type PnlWidgetLayout,
  PNL_WIDGET_VALIDATION_MESSAGES,
  type PnlWidgetIssueCode,
} from "../../lib/pnl/pnlWidgetSchema";
import type { PnlGroupBy } from "../../lib/pnl/analyticsClient";
import { resolveMetricRole, resolveMetricUnit, type PnlMetricDefinition } from "../../lib/pnl/pnlAnalytics";
import type { DatePresetName } from "../../lib/pnl/fiscalYear";
import type { PnlMetricOption } from "./metricOption";
import { PNL_GROUP_BY_VOCABULARY } from "./groupByVocabulary";
import { resolveDataGrain, type PnlCustomDatePreset } from "./dashboardRequestKeys";

export const CHART_TYPE_OPTIONS: { value: PnlChartType; label: string }[] = [
  { value: "KPI", label: "Single number" },
  { value: "bar", label: "Bar chart" },
  { value: "line", label: "Line chart" },
  { value: "scatter", label: "Scatter chart" },
  { value: "stacked_bar", label: "Stacked bar chart" },
  { value: "table", label: "Table" },
  { value: "month_on_month", label: "Month on month" },
  { value: "grid", label: "Grid (projects by month)" },
];

/**
 * The order the Group by control offers its grains in, coarsest first, then
 * time, then the people and the categories a project belongs to.
 *
 * `project_month` is deliberately absent: it is in the `PnlWidgetGroupBy`
 * union (it comes from `PnlRollupGrain`) but has never been offered here, and
 * a widget grouped by every project-month at once is a table nobody asked for.
 * It still has an entry in the vocabulary, because a saved config can carry
 * it and the widget must still be able to say how it is grouped.
 */
const GROUP_BY_ORDER: readonly PnlWidgetGroupBy[] = [
  "company",
  "project",
  "month",
  "quarter",
  "calendar_year",
  "financial_year",
  "builder",
  "project_manager",
  "site_manager",
  "status",
];

/**
 * The Group by control's options, including the three grains phase 2 adds
 * (plan task P23).
 *
 * Labels are READ from the shared vocabulary rather than typed here, so the
 * option text, the tooltip's grouping line, the caption under a widget and a
 * table widget's first column heading cannot describe the same grouping two
 * different ways (groupByVocabulary.ts).
 */
export const GROUP_BY_OPTIONS: { value: PnlWidgetGroupBy; label: string }[] = GROUP_BY_ORDER.map((value) => ({
  value,
  label: PNL_GROUP_BY_VOCABULARY[value].label,
}));

/** The plain-language line shown under the Group by control for the current selection. */
export function groupByHint(groupBy: PnlWidgetGroupBy): string {
  return PNL_GROUP_BY_VOCABULARY[groupBy].hint;
}

export const AGGREGATION_LABELS: Record<PnlAggregation, string> = {
  sum: "Sum",
  avg: "Average",
  min: "Minimum",
  max: "Maximum",
  latest: "Latest",
};

/**
 * One line under the Average option, because an unweighted mean over
 * project-months will not match the KPI card's sum-then-divide figure and
 * both are correct (plan section 12, risk 5).
 */
export const AGGREGATION_HINTS: Partial<Record<PnlAggregation, string>> = {
  avg: "An unweighted mean over the project-months in range, so it can differ from a sum-then-divide total.",
  latest: "A snapshot of the most recent month that reported, not a total across the range.",
};

export const COMPARISON_OPTIONS: { value: PnlComparisonKind | ""; label: string }[] = [
  { value: "", label: "No comparison" },
  { value: "previous_month", label: "Previous month" },
  { value: "previous_quarter", label: "Previous quarter" },
  { value: "previous_year", label: "Previous year" },
  { value: "previous_financial_year", label: "Previous financial year" },
];

export const DATE_PRESET_LABELS: Record<DatePresetName, string> = {
  this_month: "This month",
  last_month: "Last month",
  quarter_to_date: "Quarter to date",
  year_to_date: "Year to date",
  financial_year_to_date: "Financial year to date",
  last_financial_year: "Last financial year",
  project_lifetime: "Project lifetime",
  custom: "Custom range",
};

export const METRIC_ROLE_CHIP: Record<string, string> = {
  financial: "Financial",
  operational: "Operational",
  calculated: "Calculated",
};

/** Chart types that draw a second series, so the secondary metric field only appears for these (plan section 7.1). */
const TWO_SERIES_CHARTS = new Set<PnlChartType>(["scatter", "line", "stacked_bar", "KPI"]);

export function supportsSecondaryMetric(chartType: PnlChartType): boolean {
  return TWO_SERIES_CHARTS.has(chartType);
}

/**
 * SEQ-193. Applied when the Chart type control switches to or from
 * "month_on_month", so a config always carries exactly the fields that mode
 * needs and drops them cleanly on the way out (the same pattern
 * `handleChartTypeChange` already uses for `secondaryMetric` when leaving a
 * two-series chart). Returns a patch, not a full config, so the caller keeps
 * doing its own `setConfig((previous) => ({ ...previous, ...patch }))`.
 */
export function monthOnMonthChartTypePatch(config: PnlWidgetConfig, chartType: PnlChartType): Partial<PnlWidgetConfig> {
  if (chartType === "month_on_month") {
    return {
      dataGrain: "month",
      metrics: monthOnMonthMetricKeys(config),
    };
  }
  if (config.chartType === "month_on_month") {
    // Code review HIGH: leaving month_on_month must clear `dataGrain` too,
    // not just `metrics`/`showChange`. `resolveDataGrain`
    // (dashboardRequestKeys.ts) treats an explicit `dataGrain` as always
    // winning over whatever `groupBy`/`metric` would derive, so a "month"
    // left behind here would silently pin the NEW chart type's fetch to
    // month grain forever, e.g. a bar chart on net profit grouped by
    // project would keep fetching one row per month instead of one per
    // project. `undefined` lets it be re-derived fresh, exactly like every
    // config that never went through month_on_month.
    return { metrics: undefined, showChange: undefined, dataGrain: undefined };
  }
  // Neither the previous nor the new chart type is month_on_month: nothing
  // to do. In particular, `dataGrain` is left exactly as it was; it belongs
  // to whichever of handleMetricChange/handleGroupByChange last derived it,
  // and this function has no business touching it for that transition.
  return {};
}

/**
 * SEQ-193. Adds or removes one metric from a month_on_month widget's row
 * list (`groupBy: "company"` mode). Keeps `metric` in sync as the first
 * entry, because that field is still what `validateWidgetConfig`'s
 * structural check and every other chart type's fallback read, and refuses
 * to remove the LAST metric, so the table can never be left with zero rows
 * to show.
 */
export function toggleMonthOnMonthMetric(config: PnlWidgetConfig, metricKey: string): PnlWidgetConfig {
  const current = monthOnMonthMetricKeys(config);
  const next = current.includes(metricKey) ? current.filter((key) => key !== metricKey) : [...current, metricKey];
  if (next.length === 0) return config;
  return { ...config, metrics: next, metric: next[0] };
}

/**
 * The RPC grain a `groupBy` selection fetches at, as written onto the config
 * by the editor's Group by control.
 *
 * ONE IMPLEMENTATION, NOT TWO. This delegates to `resolveDataGrain`, the
 * planner's own rule (dashboardRequestKeys.ts), rather than restating it.
 * The editor writes `dataGrain` and the planner reads it, so two functions
 * holding separate opinions about the same question would only ever agree by
 * coincidence, and the first time they drifted a widget would fetch at a
 * grain the editor never intended. The rule itself lives with the planner,
 * because the planner is what has to honour it.
 *
 * DEVIATION FROM D16, STATED IN THE OPEN. The plan has the control write
 * `dataGrain` and derive `groupBy`; this writes `groupBy` and derives
 * `dataGrain`, because `groupBy` is what the user is actually choosing (how
 * the chart is bucketed) and a derived value stored in two places is how a
 * reorder and a resize end up disagreeing, which is the same reasoning D18
 * uses for order and size.
 */
export function deriveDataGrain(
  groupBy: PnlWidgetGroupBy,
  metricKey: string,
  registry?: readonly PnlMetricDefinition[],
): PnlGroupBy {
  return resolveDataGrain({ groupBy, metric: metricKey, dataGrain: undefined }, registry) as PnlGroupBy;
}

/** A metric's registry definition, or undefined when the key is unknown. */
export function findMetric(metrics: PnlMetricOption[], metricKey: string): PnlMetricOption | undefined {
  return metrics.find((m) => m.metricKey === metricKey);
}

/**
 * Clamps an aggregation to what the metric permits, so changing the metric
 * can never leave an impossible pair on the draft. Falls back to the first
 * permitted value, which for a calculated metric is Average.
 */
export function clampAggregation(
  aggregation: PnlAggregation,
  def: PnlMetricDefinition | undefined,
  secondaryDef?: PnlMetricDefinition,
): PnlAggregation {
  if (!def) return aggregation;
  const permitted = permittedAggregationsFor(def, secondaryDef);
  // M4: an empty intersection means no aggregation fits both metrics; keep
  // the draft's value so the validator reports the pairing rather than a
  // silent fallback hiding it.
  if (permitted.length === 0) return aggregation;
  return permitted.includes(aggregation) ? aggregation : permitted[0];
}

export interface MetricSelectorGroup {
  section: PnlMetricDefinition["section"];
  label: string;
  options: PnlMetricOption[];
}

const SECTION_LABELS: Record<PnlMetricDefinition["section"], string> = {
  revenue: "Revenue",
  budget: "Budget",
  expense: "Expenses",
  labour: "Labour",
  calculated: "Calculated",
};

const SECTION_ORDER: PnlMetricDefinition["section"][] = ["revenue", "budget", "expense", "labour", "calculated"];

/**
 * Groups the vocabulary by registry section for the picker, filtered by a
 * search that matches display name OR key, so someone who knows the key can
 * type it and someone who does not can type the words (plan section 7.2).
 * Operational metrics are never hidden: putting them in the same picker is
 * what SEQ-182 AC07 asked for.
 */
export function groupMetricsForSelector(metrics: PnlMetricOption[], query: string): MetricSelectorGroup[] {
  const needle = query.trim().toLowerCase();
  const matches = needle
    ? metrics.filter(
        (m) => m.displayName.toLowerCase().includes(needle) || m.metricKey.toLowerCase().includes(needle),
      )
    : metrics;

  return SECTION_ORDER.map((section) => ({
    section,
    label: SECTION_LABELS[section],
    options: matches
      .filter((m) => m.section === section)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName)),
  })).filter((group) => group.options.length > 0);
}

/** The chips one option shows: its unit and its role (plan section 7.2). */
export function metricChips(metric: PnlMetricOption): { unit: string; role: string } {
  return {
    unit: resolveMetricUnit(metric) === "currency" ? "$" : UNIT_CHIP[resolveMetricUnit(metric)],
    role: METRIC_ROLE_CHIP[resolveMetricRole(metric)] ?? "Financial",
  };
}

const UNIT_CHIP: Record<string, string> = {
  currency: "$",
  percent: "%",
  hours: "hrs",
  count: "count",
  ratio: "ratio",
  currency_per_hour: "$/hr",
};

export interface DraftValidation {
  /** False only when the config cannot be saved. A soft issue leaves this true. */
  valid: boolean;
  issues: PnlWidgetIssue[];
  /** Findings with no section 7.3 message, shown once at the foot of the form rather than under a field. */
  otherErrors: string[];
  /**
   * Issues that do NOT block a save: the config is readable, the widget
   * renders, and the message is shown so the user can act on it. Today that
   * is exactly `comparison_on_lifetime` (team lead decision on the P8
   * report), where the widget runs without its comparison rather than being
   * dropped from the dashboard.
   */
  softIssues: PnlWidgetIssue[];
}

/** Issue codes that are shown but never block a save or hide a widget. */
const SOFT_ISSUE_CODES: ReadonlySet<string> = new Set(["comparison_on_lifetime"]);

export function isSoftIssue(issue: PnlWidgetIssue): boolean {
  return SOFT_ISSUE_CODES.has(issue.code);
}

/**
 * Runs both validators over a draft and merges their findings. Structural
 * first (it can reject a shape the registry check would then read wrongly),
 * then the registry-aware one only when the shape holds.
 *
 * `valid` comes from the validators themselves, never from counting issues: a
 * soft issue is attached to a config that is still valid, and treating any
 * issue as blocking would put Save out of reach for a widget the page is
 * perfectly happy to render.
 */
/**
 * E2E low 2: the validators' `errors` carry a developer string for the rules
 * that ALSO raise a 7.3 issue (e.g. "name must be a non-empty string" beside
 * "Give the widget a name."), so matching on text alone rendered both. An
 * error is covered when its text equals an issue's message OR its rule is
 * one an issue of that code already represents, matched here by the
 * validator's own error shape.
 */
const ERROR_SHAPE_BY_ISSUE_CODE: Partial<Record<PnlWidgetIssueCode, RegExp>> = {
  blank_name: /^name must be a non-empty string$/,
  custom_range_incomplete: /^dateRange\.custom/,
  unknown_metric: / is not a known registry key$/,
  mixed_units: /have different units;/,
};

function coveredByIssue(error: string, issues: PnlWidgetIssue[]): boolean {
  return issues.some((issue) => {
    if (issue.message === error) return true;
    const shape = ERROR_SHAPE_BY_ISSUE_CODE[issue.code];
    return shape !== undefined && shape.test(error);
  });
}

/**
 * E2E low 3: the validator only knows metric keys, so its mixed-units issue
 * names "calculated.net_profit ($)". The drawer has the loaded registry, so
 * the same catalogue function is re-run here with display names, giving the
 * plan's "Net Profit ($) and Overtime Hours (hrs) cannot share one axis."
 */
function withDisplayNames(issue: PnlWidgetIssue, config: PnlWidgetConfig, metrics: PnlMetricOption[]): PnlWidgetIssue {
  if (issue.code !== "mixed_units" || !config.secondaryMetric) return issue;
  const primary = findMetric(metrics, config.metric);
  const secondary = findMetric(metrics, config.secondaryMetric);
  if (!primary || !secondary) return issue;
  return {
    ...issue,
    message: PNL_WIDGET_VALIDATION_MESSAGES.mixedUnits(
      primary.displayName,
      resolveMetricUnit(primary),
      secondary.displayName,
      resolveMetricUnit(secondary),
    ),
  };
}

export function validateDraft(config: PnlWidgetConfig, metrics: PnlMetricOption[]): DraftValidation {
  const structural = validateWidgetConfig(config);
  const issues = [...structural.issues];
  const otherErrors = structural.errors.filter((error) => !coveredByIssue(error, structural.issues));
  let valid = structural.valid;

  if (structural.valid && metrics.length > 0) {
    const compatibility = validateWidgetMetricCompatibility(config, metrics);
    issues.push(...compatibility.issues.map((issue) => withDisplayNames(issue, config, metrics)));
    valid = compatibility.valid;
    for (const error of compatibility.errors) {
      if (!coveredByIssue(error, compatibility.issues)) otherErrors.push(error);
    }
  }

  return { valid, issues, otherErrors: [...new Set(otherErrors)], softIssues: issues.filter(isSoftIssue) };
}

/** The message to render under one field, or null. */
export function issueFor(issues: PnlWidgetIssue[], field: PnlWidgetIssueField): string | null {
  return issues.find((issue) => issue.field === field)?.message ?? null;
}

/** A widgetId unique within one dashboard, readable in the jsonb column rather than an opaque uuid. */
export function makeWidgetId(chartType: PnlChartType, existingIds: string[]): string {
  const base = `${chartType.toLowerCase()}-custom`;
  let candidate = base;
  let counter = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}-${counter}`;
    counter += 1;
  }
  return candidate;
}

export interface WidgetDraft {
  config: PnlWidgetConfig;
  layout: PnlWidgetLayout;
}

/** A new widget starts as a project-grain bar on net profit: the safest thing to draw with no configuration at all. */
export function newWidgetDraft(existingIds: string[]): WidgetDraft {
  return {
    config: {
      schemaVersion: 1,
      widgetId: makeWidgetId("bar", existingIds),
      name: "New widget",
      chartType: "bar",
      metric: "calculated.net_profit",
      groupBy: "project",
      dataGrain: "project",
      aggregation: "sum",
      filters: [],
      dateRange: { preset: "financial_year_to_date" },
      displayAs: "currency",
      layout: { w: 2, h: 2 },
    },
    layout: { w: 2, h: 2 },
  };
}

/** The date range options for one dashboard: the eight built-in presets plus that dashboard's own saved ranges (D19). */
export interface DateRangeChoice {
  value: string;
  label: string;
}

export const DASHBOARD_RANGE_VALUE = "__dashboard__";

export function dateRangeChoices(presets: PnlCustomDatePreset[]): DateRangeChoice[] {
  const builtIn = (Object.keys(DATE_PRESET_LABELS) as DatePresetName[]).map((preset) => ({
    value: preset,
    label: DATE_PRESET_LABELS[preset],
  }));
  return [
    { value: DASHBOARD_RANGE_VALUE, label: "Follow the dashboard filter" },
    ...builtIn,
    ...presets.map((preset) => ({ value: `preset:${preset.id}`, label: preset.name })),
  ];
}

/** The value the Date range control shows for a config. */
export function dateRangeValueOf(config: PnlWidgetConfig): string {
  if (config.dateRangeMode !== "widget") return DASHBOARD_RANGE_VALUE;
  if (config.dateRange.customPresetId) return `preset:${config.dateRange.customPresetId}`;
  return config.dateRange.preset;
}

/** Applies a Date range selection back onto a config. */
export function applyDateRangeChoice(config: PnlWidgetConfig, value: string): PnlWidgetConfig {
  if (value === DASHBOARD_RANGE_VALUE) {
    return { ...config, dateRangeMode: "dashboard", dateRange: { preset: config.dateRange.preset } };
  }
  if (value.startsWith("preset:")) {
    return {
      ...config,
      dateRangeMode: "widget",
      dateRange: { preset: "custom", customPresetId: value.slice("preset:".length) },
    };
  }
  const preset = value as DatePresetName;
  if (preset === "custom") {
    return { ...config, dateRangeMode: "widget", dateRange: { preset: "custom", custom: { from: "", to: "" } } };
  }
  return { ...config, dateRangeMode: "widget", dateRange: { preset } };
}

/** Every widgetId the default dashboard uses, so a new widget never collides with one the renderer treats specially. */
export const RESERVED_WIDGET_IDS: readonly string[] = PHASE1_WIDGETS.map((w) => w.widgetId);
